import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getActionPresetsOptions,
  getActionPresetsQueryKey,
  getActionPresetOptions,
  getActionPresetQueryKey,
  createActionPresetMutation,
  updateActionPresetMutation,
  deleteActionPresetMutation,
} from '@/shared/api';

export interface ActionPresetQueryParams {
  platform?: string;
  active?: boolean;
}

export interface ActionPresetDetailParams {
  id: number;
}

/**
 * Query factory for action presets
 */
export const actionPresetQueries = {
  all: (params?: ActionPresetQueryParams) => getActionPresetsOptions({ query: params }),
  allKey: (params?: ActionPresetQueryParams) => getActionPresetsQueryKey({ query: params }),
  detail: (params: ActionPresetDetailParams) => getActionPresetOptions({ path: { id: params.id } }),
  detailKey: (params: ActionPresetDetailParams) => getActionPresetQueryKey({ path: { id: params.id } }),
};

/**
 * Hook to fetch all action presets
 */
export const useActionPresets = (params?: ActionPresetQueryParams, enabled: boolean = true) => {
  return useQuery({
    ...actionPresetQueries.all(params),
    enabled,
  });
};

/**
 * Hook to fetch a single action preset by ID
 */
export const useActionPreset = (params: ActionPresetDetailParams, enabled: boolean = true) => {
  return useQuery({
    ...actionPresetQueries.detail(params),
    enabled,
  });
};

/**
 * Hook to create a new action preset
 */
export const useCreateActionPreset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    ...createActionPresetMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: actionPresetQueries.allKey() });
    },
  });
};

/**
 * Hook to update an existing action preset
 */
export const useUpdateActionPreset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    ...updateActionPresetMutation(),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: actionPresetQueries.allKey() });
      if (variables.path?.id) {
        queryClient.invalidateQueries({
          queryKey: actionPresetQueries.detailKey({ id: variables.path.id })
        });
      }
    },
  });
};

/**
 * Hook to delete an action preset
 */
export const useDeleteActionPreset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    ...deleteActionPresetMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: actionPresetQueries.allKey() });
    },
  });
};
